<template>
  <AIBasePanel
    :title="getLocaleText('ai_boolean_creator_chat_history.title')"
    @close="emit('close')"
    @home="emit('home')"
  >
    <template v-if="formattedChatHistory">
      <AIAssistantBaseOptions
        v-for="(messages, date) in formattedChatHistory"
        :key="date"
        class="tw:mb-5"
        :title="date"
      >
        <AIAssistantBaseOption
          v-for="message in messages"
          :key="message.id"
          class="tw:group tw:mb-2"
          :data-testid="`chat-message-group-${message.group_id}`"
          @click="openChatHistory(message.group_id)"
        >
          <div class="tw:w-[430px] tw:truncate">
            {{ extractPrompt(message.message, message.source) }}
          </div>
          <div class="tw:ml-auto tw:font-bold tw:shrink-0">
            <BaseIcon
              v-if="isDesktop"
              class="tw:rounded-full tw:p-[4px] tw:text-lg tw:opacity-0 tw:transition-opacity tw:duration-200 tw:hover:bg-dark-blue-200 tw:group-hover:opacity-100"
              data-testid="chat-history-delete-btn"
              icon="bin"
              @click.stop="handleChatDelete(message.group_id)"
            />
            {{ formatDisplayDate(message.created_at) }}
          </div>
        </AIAssistantBaseOption>
      </AIAssistantBaseOptions>
    </template>

    <template #footer>
      <div class="tw:flex tw:w-full tw:items-center tw:justify-center tw:py-3">
        <Pagination
          v-if="pagination.pageCount > 1"
          data-testid="chat-history-pagination"
          hide-page-size-options
          :pagination="pagination"
          @pagination-changed="onHandlePaginationChanged"
        />
      </div>
    </template>
  </AIBasePanel>
</template>

<script setup lang="ts">
import { differenceInDays, format, parseISO, startOfDay } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { storeToRefs } from "pinia";
import { computed, inject, onMounted, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseOption from "shared/components/AI/AIAssistant/AIAssistantBaseOption.vue";
import AIAssistantBaseOptions from "shared/components/AI/AIAssistant/AIAssistantBaseOptions.vue";
import AIBasePanel from "shared/components/AI/AIBasePanel.vue";
import BaseIcon from "shared/components/base/BaseIcon.vue";
import Pagination from "shared/components/Pagination/Pagination.vue";
import { type PaginationData } from "shared/components/Pagination/types";
import DialogService from "shared/modals/DialogService";
import { DialogTheme } from "shared/modals/types";
import { useAIStore } from "shared/stores/ai";
import { useUserStore } from "shared/stores/user";
import type { Nullable } from "shared/types";

interface ChatMessage {
  id: number;
  user_id: number;
  organisation_id: number;
  role: string;
  message: string;
  refinements: Nullable<string>;
  source: string;
  group_id: number;
  created_at: string;
  updated_at: string;
  ai_feature_configuration_id: number;
  archived_at: Nullable<string>;
}

const CHAT_PER_PAGE = 50;

const isDesktop = inject("isDesktop", false);

const emit = defineEmits(["close", "home", "delete"]);
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const aiStore = useAIStore();
const { openAIModal } = aiStore;

const chatHistories = ref<ChatMessage[]>([]);

const pagination = ref<PaginationData>({
  page: 1,
  pageSize: CHAT_PER_PAGE,
  pageCount: 0,
});

const relativeDateLabels = computed(() => ({
  today: getLocaleText("ai_boolean_creator_chat_history.relative_date.today"),
  oneDayAgo: getLocaleText(
    "ai_boolean_creator_chat_history.relative_date.one_day_ago"
  ),
  twoDaysAgo: getLocaleText(
    "ai_boolean_creator_chat_history.relative_date.two_days_ago"
  ),
  pastWeek: getLocaleText(
    "ai_boolean_creator_chat_history.relative_date.past_week"
  ),
  pastMonth: getLocaleText(
    "ai_boolean_creator_chat_history.relative_date.past_month"
  ),
  older: getLocaleText("ai_boolean_creator_chat_history.relative_date.older"),
}));

function handleChatDelete(groupId: number) {
  DialogService.confirm({
    theme: DialogTheme.DANGER,
    title: getLocaleText("ai_boolean_creator_chat_history.delete_dialog.title"),
    message: getLocaleText(
      "ai_boolean_creator_chat_history.delete_dialog.message"
    ),
  })
    .onCancel(() => {})
    .onOk(async () => {
      await deleteChatHistory(groupId);
    });
}

async function deleteChatHistory(groupId: number) {
  try {
    await $streemApiV1.delete(`ai/chat_histories/group_archive/${groupId}`);
    await getChatHistoryList();
  } catch (error) {
    ErrorDialog(
      getLocaleText("ai_boolean_creator_chat_history.fail_to_delete"),
      error
    );
  }
}

async function getChatHistoryList() {
  const limit = CHAT_PER_PAGE;
  const offset = (pagination.value.page - 1) * limit;

  try {
    const response = await $streemApiV1.get("ai/chat_histories", {
      params: { limit, offset },
    });

    chatHistories.value = response.data;

    pagination.value.pageCount = parseInt(
      response.headers["x-total-pages"],
      10
    );
  } catch (error) {
    ErrorDialog(
      getLocaleText("ai_boolean_creator_chat_history.fail_to_load_history"),
      error
    );
  }
}

function openChatHistory(groupId: number) {
  aiStore.aiBooleanCreatorChatHistoryGroupId = groupId;
  openAIModal("AIChatHistoryPanel");
}

function extractPrompt(message: string, source: string): string {
  try {
    const chatMessage = JSON.parse(message);

    if (source === "stream_summary") {
      return getLocaleText(
        "ai_boolean_creator_chat_history.mention_stream_title",
        { title: chatMessage.title }
      );
    }

    return (
      chatMessage.prompt ||
      getLocaleText("ai_boolean_creator_chat_history.unknown_prompt")
    );
  } catch {
    return getLocaleText(
      "ai_boolean_creator_chat_history.invalid_message_format"
    );
  }
}

function getRelativeDate(dateString: string, timeZone: string) {
  const date = startOfDay(utcToZonedTime(parseISO(dateString), timeZone));
  const today = startOfDay(utcToZonedTime(new Date(), timeZone));
  const diff = differenceInDays(today, date);
  if (diff === 0) return relativeDateLabels.value.today;
  if (diff === 1) return relativeDateLabels.value.oneDayAgo;
  if (diff === 2) return relativeDateLabels.value.twoDaysAgo;
  if (diff <= 7) return relativeDateLabels.value.pastWeek;
  if (diff <= 30) return relativeDateLabels.value.pastMonth;

  return relativeDateLabels.value.older;
}

function formatDisplayDate(dateString: string): string {
  return format(parseISO(dateString), "d MMM");
}

const formattedChatHistory = computed<Record<string, ChatMessage[]>>(() => {
  if (!chatHistories.value) return {};
  const grouped: Record<string, ChatMessage[]> = {};

  Object.values(chatHistories.value)
    .flat()
    .forEach((message) => {
      const relativeDate = getRelativeDate(
        message.created_at,
        currentUser.value.time_zone
      );

      if (!grouped[relativeDate]) grouped[relativeDate] = [];
      grouped[relativeDate].push(message);
    });

  const dateOrder = [
    relativeDateLabels.value.today,
    relativeDateLabels.value.oneDayAgo,
    relativeDateLabels.value.twoDaysAgo,
    relativeDateLabels.value.pastWeek,
    relativeDateLabels.value.pastMonth,
    relativeDateLabels.value.older,
  ];

  // Groups and chats are always sorted regardless of the order coming from backend.
  return Object.fromEntries(
    Object.entries(grouped)
      .sort(
        ([dateA], [dateB]) =>
          dateOrder.indexOf(dateA) - dateOrder.indexOf(dateB)
      )
      .map(([date, messages]) => [
        date,
        messages.sort(
          (dateA, dateB) =>
            new Date(dateB.created_at).getTime() -
            new Date(dateA.created_at).getTime()
        ),
      ])
  );
});

function onHandlePaginationChanged(updatedPagination: PaginationData) {
  pagination.value = updatedPagination;
  getChatHistoryList();
}

onMounted(() => {
  getChatHistoryList();
});
</script>
